import React, { useState } from 'react'
import { BellPlus, CalendarClock, Edit3, Loader2, Mail, MapPin, Plus, ShieldCheck, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { vehicleControlCenterService } from '../../services/vehicleControlCenterService'
import type { AssociationSourceType, ControlCenterRule, ControlCenterSource } from '../../services/vehicleControlCenterTypes'

interface Props {
  rules: ControlCenterRule[]
  loading?: boolean
  error?: string | null
  onRefresh: () => Promise<void> | void
  onCreateRule?: () => void
  onEditRule?: (rule: ControlCenterRule) => void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const sourceType = (source?: ControlCenterSource | AssociationSourceType | string): string =>
  !source ? 'DIRECT' : typeof source === 'string' ? source : source.source_type || source.type || 'DIRECT'

const formatValue = (value: unknown): string => {
  if (value == null || value === '') return '—'
  if (typeof value === 'string') return value
  try { return JSON.stringify(value) } catch { return String(value) }
}

const recipients = (rule: ControlCenterRule): string =>
  (rule.recipients || []).map(recipient =>
    typeof recipient === 'string' ? recipient : recipient.name || recipient.address || recipient.email || recipient.phone || ''
  ).filter(Boolean).join(', ') ||
  ((rule.conditions?.diffusion as { roles?: string[] } | undefined)?.roles || []).join(', ') ||
  '—'

const schedule = (rule: ControlCenterRule, alwaysLabel: string): string => {
  if (rule.schedule) return formatValue(rule.schedule)
  const parts = [
    rule.active_days?.length ? rule.active_days.join(', ') : null,
    rule.active_hours_start || rule.active_hours_end
      ? `${rule.active_hours_start || '00:00'}–${rule.active_hours_end || '23:59'}`
      : null,
    rule.active_date_start || rule.active_date_end
      ? `${rule.active_date_start || '…'}–${rule.active_date_end || '…'}`
      : null
  ].filter(Boolean)
  return parts.join(' · ') || alwaysLabel
}

const VehicleRulesTab: React.FC<Props> = ({ rules, loading, error, onRefresh, onCreateRule, onEditRule, showMessage }) => {
  const { t } = useTranslation()
  const [busyId, setBusyId] = useState<number | null>(null)

  const toggle = async (rule: ControlCenterRule) => {
    const enabled = rule.is_active ?? rule.enabled ?? false
    setBusyId(rule.id)
    try {
      await vehicleControlCenterService.setRuleEnabled(rule.id, !enabled)
      showMessage?.(t('vcc_rule_updated', { defaultValue: 'Alert rule updated' }), 'success')
      await onRefresh()
    } catch (e: any) {
      showMessage?.(e?.response?.data?.error || t('vcc_rule_update_failed', { defaultValue: 'Failed to update alert rule' }), 'error')
    } finally {
      setBusyId(null)
    }
  }

  if (loading) return <div className="flex h-56 items-center justify-center gap-2 text-gray-500"><Loader2 className="animate-spin" />{t('vcc_loading', { defaultValue: 'Loading control center…' })}</div>
  if (error) return <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-center text-red-800"><p>{error}</p><button onClick={() => onRefresh()} className="mt-3 rounded-lg bg-red-700 px-3 py-2 text-sm text-white">{t('retry', { defaultValue: 'Retry' })}</button></div>

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('vcc_alert_rules', { defaultValue: 'Alert Rules' })}</h3>
          <p className="text-sm text-gray-500">{t('vcc_rules_hint', { defaultValue: 'Direct and inherited rules that are effective for this vehicle.' })}</p>
        </div>
        <button onClick={onCreateRule} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"><Plus size={16} />{t('vcc_create_rule', { defaultValue: 'Create rule' })}</button>
      </div>

      {rules.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 py-14 text-center text-gray-500">
          <ShieldCheck className="mx-auto mb-3 text-gray-300" size={40} />
          <p className="font-medium text-gray-700">{t('vcc_no_rules', { defaultValue: 'No alert rules apply to this vehicle' })}</p>
          <button onClick={onCreateRule} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600"><BellPlus size={15} />{t('vcc_create_first_rule', { defaultValue: 'Create the first rule' })}</button>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {rules.map(rule => {
            const enabled = rule.is_active ?? rule.enabled ?? false
            const source = sourceType(rule.source || rule.sources?.[0]).toUpperCase()
            const editable = (rule.can_edit ?? !rule.is_read_only) && source === 'DIRECT'
            const zone = rule.zone_name || rule.geofence_name || rule.corridor_name || '—'
            return (
              <article key={rule.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className={`rounded-lg p-2 ${enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}><ShieldCheck size={19} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-gray-900">{rule.name}</h4>
                      <span className="rounded bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-800">{source}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                        rule.severity === 'CRITICAL' ? 'border-red-200 bg-red-50 text-red-700' :
                          rule.severity === 'INFO' ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-amber-200 bg-amber-50 text-amber-700'
                      }`}>{rule.severity || 'WARNING'}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{rule.description || rule.rule_type || rule.alert_type || rule.trigger || '—'}</p>
                  </div>
                  <button type="button" role="switch" aria-checked={enabled} disabled={!editable || busyId === rule.id} onClick={() => toggle(rule)}
                    title={!editable ? t('vcc_manage_rule_source', { defaultValue: 'Inherited rules must be managed at their source' }) as string : undefined}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <dl className="mt-4 grid gap-3 border-t border-gray-100 pt-3 text-xs sm:grid-cols-2">
                  <div className="flex gap-2"><BellPlus size={14} className="shrink-0 text-gray-400" /><div><dt className="font-semibold text-gray-500">{t('vcc_trigger', { defaultValue: 'Trigger' })}</dt><dd className="text-gray-800">{rule.rule_type || rule.alert_type || rule.trigger || formatValue(rule.conditions)}</dd></div></div>
                  <div className="flex gap-2"><MapPin size={14} className="shrink-0 text-gray-400" /><div><dt className="font-semibold text-gray-500">{t('vcc_zone', { defaultValue: 'Zone' })}</dt><dd className="text-gray-800">{zone}</dd></div></div>
                  <div className="flex gap-2"><Mail size={14} className="shrink-0 text-gray-400" /><div><dt className="font-semibold text-gray-500">{t('vcc_channels', { defaultValue: 'Channels' })}</dt><dd className="text-gray-800">{rule.channels?.join(', ') || '—'}</dd></div></div>
                  <div className="flex gap-2"><Users size={14} className="shrink-0 text-gray-400" /><div><dt className="font-semibold text-gray-500">{t('vcc_recipients', { defaultValue: 'Recipients' })}</dt><dd className="break-all text-gray-800">{recipients(rule)}</dd></div></div>
                  <div className="flex gap-2 sm:col-span-2"><CalendarClock size={14} className="shrink-0 text-gray-400" /><div><dt className="font-semibold text-gray-500">{t('vcc_schedule', { defaultValue: 'Schedule' })}</dt><dd className="text-gray-800">{schedule(rule, t('vcc_always', { defaultValue: 'Always' }))}</dd></div></div>
                </dl>
                <div className="mt-3 flex items-center justify-between">
                  <span className={`text-xs font-semibold ${enabled ? 'text-emerald-700' : 'text-gray-500'}`}>{enabled ? t('enabled', { defaultValue: 'Enabled' }) : t('disabled', { defaultValue: 'Disabled' })}</span>
                  {editable ? (
                    <button onClick={() => onEditRule?.(rule)} className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"><Edit3 size={14} />{t('edit')}</button>
                  ) : <span className="text-[11px] text-gray-500">{t('vcc_inherited_rule_hint', { defaultValue: 'Manage at source' })}</span>}
                </div>
              </article>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default VehicleRulesTab
